import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useRole } from '@/context/RoleContext';
import { API_MODE, api, apiList } from '@/utils/api';
import { formatDate, formatDateTime } from '@/utils/format';
import {
  anomalyToRecipient,
  applyRowValidation,
  bulkTotals,
  candidateMatches,
  candidateToRecipient,
  defaultBulkSchedule,
  rehydrateList,
  summarizeRows,
} from '@/utils/bulkPayments';
import type {
  BulkCandidate,
  BulkChannel,
  BulkRecipient,
  BulkSchedule,
  BulkTransfer,
  BulkUploadAnomaly,
  Campaign,
  FundingWallet,
  SavedRecipientList,
  Wallet,
} from '@/types/portal';
import BulkStepsHeader from './components/BulkStepsHeader';
import UploadStep, { type BulkMethod } from './components/UploadStep';
import type { CampaignOption } from './components/CampaignPullPanel';
import ReviewStep from './components/ReviewStep';
import ConfirmStep from './components/ConfirmStep';
import BulkTransferSuccess from './components/BulkTransferSuccess';
import BulkTransferHistory from './components/BulkTransferHistory';
import ValidationReportModal from './components/ValidationReportModal';
import RescheduleBatchModal from './components/RescheduleBatchModal';
import CancelBatchModal from './components/CancelBatchModal';

interface PoolAccount {
  accountName: string;
  accountNumber: string;
  bankName: string;
}

/** The API sends ISO timestamps; the history and receipt show "16 Sep 2026" and "17 Sep 2026, 09:00". */
const forDisplay = (transfer: BulkTransfer): BulkTransfer => ({
  ...transfer,
  createdAt: formatDate(transfer.createdAt),
  scheduledFor: transfer.scheduledFor ? formatDateTime(transfer.scheduledFor) : undefined,
});

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export default function BulkPayments() {
  const navigate = useNavigate();
  const { can } = useRole();
  const canManage = can('payments.manage');
  const canApprove = can('payments.approve');

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [walletId, setWalletId] = useState('');
  const [channel, setChannel] = useState<BulkChannel>('wallet');
  const [method, setMethod] = useState<BulkMethod>('manual');
  const [recipients, setRecipients] = useState<BulkRecipient[]>([]);
  const [description, setDescription] = useState('Twiva Payment');
  const [uniformAmount, setUniformAmount] = useState(0);
  const [sms, setSms] = useState(true);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [candidates, setCandidates] = useState<BulkCandidate[]>([]);
  const [pool, setPool] = useState<PoolAccount | null>(null);
  const [campaignList, setCampaignList] = useState<Campaign[]>([]);
  const [savedLists, setSavedLists] = useState<SavedRecipientList[]>([]);
  const [transfers, setTransfers] = useState<BulkTransfer[]>([]);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [schedule, setSchedule] = useState<BulkSchedule>(defaultBulkSchedule());
  const [otp, setOtp] = useState('');
  const [completed, setCompleted] = useState<BulkTransfer | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<{ label: string; backLabel: string } | null>(null);
  const [rescheduling, setRescheduling] = useState<BulkTransfer | null>(null);
  const [cancelling, setCancelling] = useState<BulkTransfer | null>(null);

  // Wallets the batch can be paid from, recent batches, saved lists and the pool account.
  useEffect(() => {
    apiList<Wallet>('/wallets?ownerType=Funding')
      .then(({ data }) => setWallets(data.filter((wallet) => wallet.status === 'Wallet active')))
      .catch((loadError: unknown) => setError(messageOf(loadError, 'Could not load the wallets.')));
    apiList<BulkTransfer>('/bulk-transfers')
      .then(({ data }) => setTransfers(data.map(forDisplay)))
      .catch((loadError: unknown) => setError(messageOf(loadError, 'Could not load recent batches.')));
    // Every campaign, so From Campaigns can list ones nobody is hired on yet.
    apiList<Campaign>('/campaigns?per_page=100')
      .then(({ data }) => setCampaignList(data))
      .catch(() => setCampaignList([]));
    apiList<SavedRecipientList>('/recipient-lists')
      .then(({ data }) => setSavedLists(data))
      .catch(() => setSavedLists([]));
    api<PoolAccount | null>('/funding/pool-account')
      .then(setPool)
      .catch(() => setPool(null));
  }, []);

  const sourceWallet = wallets.find((wallet) => wallet.id === walletId) ?? null;
  const currency = sourceWallet?.currency ?? '';
  const walletName = sourceWallet?.ownerName ?? '';
  const totals = useMemo(() => bulkTotals(recipients), [recipients]);

  const fundingWallets = useMemo<FundingWallet[]>(
    () =>
      wallets.map((wallet) => ({
        id: wallet.id,
        ownerType: 'Funding',
        currency: wallet.currency,
        balance: wallet.balance,
        status: wallet.status,
        rail: wallet.market,
      })),
    [wallets],
  );

  // Who can be paid depends on the source wallet (production resolves wallets against it).
  useEffect(() => {
    if (!walletId || !currency) {
      setCandidates([]);
      return;
    }
    let current = true;
    apiList<BulkCandidate>(
      `/bulk-transfers/candidates?currency=${encodeURIComponent(currency)}&sourceWalletId=${encodeURIComponent(walletId)}`,
    )
      .then(({ data }) => current && setCandidates(data))
      .catch((loadError: unknown) => current && setError(messageOf(loadError, 'Could not load the people you can pay.')));
    return () => {
      current = false;
    };
  }, [walletId, currency]);

  // Hired creators who aren't payable yet: shown in search with the reason, never added.
  const payableCandidates = useMemo(() => candidates.filter((candidate) => !candidate.waiting), [candidates]);
  // Everyone on a campaign who can't be paid yet, hired or not (From Campaigns shows them all).
  const campaignWaiting = useMemo(
    () => (currency ? candidates.filter((candidate) => candidate.waiting && candidateMatches(candidate, channel, currency)) : []),
    [candidates, currency, channel],
  );
  // Hired but not payable yet: what the summary counts and the search lists.
  const waitingCandidates = useMemo(() => campaignWaiting.filter((candidate) => !candidate.notHired), [campaignWaiting]);

  // Payable creators for this wallet's currency and channel, whether or not they're in the batch yet.
  const readyCandidates = useMemo(
    () => (currency ? payableCandidates.filter((candidate) => candidateMatches(candidate, channel, currency)) : []),
    [payableCandidates, currency, channel],
  );

  const addableCandidates = useMemo(() => {
    if (!currency) return [] as BulkCandidate[];
    return payableCandidates.filter(
      (candidate) =>
        candidateMatches(candidate, channel, currency) &&
        !recipients.some((row) => row.id === candidate.id),
    );
  }, [payableCandidates, currency, channel, recipients]);

  const campaignOptions = useMemo<CampaignOption[]>(() => {
    if (!currency) return [];
    // Every campaign in the wallet's currency, so one with nobody ready can be opened to see why.
    const map = new Map<string, CampaignOption>();
    const entry = (name: string) => {
      const existing = map.get(name);
      if (existing) return existing;
      const created = { id: name, name, currency, count: 0, waiting: 0, notHired: 0 };
      map.set(name, created);
      return created;
    };
    campaignList.filter((campaign) => campaign.currency === currency).forEach((campaign) => entry(campaign.name));
    readyCandidates.forEach((candidate) => {
      entry(candidate.campaign).count += 1;
    });
    campaignWaiting.forEach((candidate) => {
      const option = entry(candidate.campaign);
      if (candidate.notHired) option.notHired += 1;
      else option.waiting += 1;
    });
    return Array.from(map.values()).sort(
      (a, b) => b.count - a.count || b.waiting - a.waiting || a.name.localeCompare(b.name),
    );
  }, [campaignList, readyCandidates, campaignWaiting, currency]);

  const resetRoster = () => {
    setRecipients([]);
    setReviewConfirmed(false);
    setOtp('');
    setReport(null);
    setSchedule(defaultBulkSchedule());
  };

  const handleWallet = (id: string) => {
    setWalletId(id);
    resetRoster();
  };

  const handleChannel = (next: BulkChannel) => {
    setChannel(next);
    resetRoster();
  };

  const addCandidate = (candidate: BulkCandidate) => {
    setRecipients((prev) => {
      if (prev.some((row) => row.id === candidate.id)) return prev;
      return [...prev, candidateToRecipient(candidate, channel, description, 'manual')];
    });
  };

  const buildFromCandidates = (
    from: BulkCandidate[],
    predicate: (candidate: BulkCandidate) => boolean,
    source: BulkRecipient['source'],
  ) =>
    from
      .filter(predicate)
      .map((candidate) => candidateToRecipient(candidate, channel, description, source));

  const handleFileParse = async (fileName: string) => {
    if (!currency) {
      setNotice('Select a source wallet before uploading a file.');
      return;
    }
    setError('');
    try {
      const result = await api<{ candidates: BulkCandidate[]; anomalies: BulkUploadAnomaly[] }>(
        '/bulk-transfers/validate',
        { method: 'POST', body: JSON.stringify({ fileName, channel, currency, sourceWalletId: walletId }) },
      );
      const parsed = buildFromCandidates(
        result.candidates,
        (candidate) => candidateMatches(candidate, channel, currency),
        'file',
      );
      const anomalies = result.anomalies.map((row, index) =>
        anomalyToRecipient(row, channel, currency, description, index),
      );
      const { rows } = applyRowValidation([...parsed, ...anomalies], channel);
      setRecipients(rows);
      setReviewConfirmed(false);
      setReport({ label: fileName, backLabel: 'Back to upload' });
    } catch (parseError) {
      setError(messageOf(parseError, 'Could not read the file.'));
    }
  };

  // Any roster can be checked before Review — file upload, manual entry or a campaign pull.
  const openPreviewReport = () => {
    if (!currency) {
      setNotice('Select a source wallet before previewing.');
      return;
    }
    if (recipients.length === 0) {
      setNotice('Add at least one recipient before previewing.');
      return;
    }
    const { rows } = applyRowValidation(recipients, channel);
    setRecipients(rows);
    setReviewConfirmed(false);
    setReport({
      label:
        method === 'campaign' ? 'your campaign pull' : method === 'file' ? 'your file' : 'your entry',
      backLabel:
        method === 'campaign' ? 'Back to campaigns' : method === 'file' ? 'Back to upload' : 'Back to entry',
    });
  };

  // Inline corrections in the report land straight back on the roster row.
  const fixRow = (id: string, patch: Partial<BulkRecipient>) => {
    setRecipients((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  // Drop every line the report is flagging in one move.
  const removeFlaggedRows = () => {
    const { flagged } = summarizeRows(recipients, channel);
    if (flagged.length === 0) return;
    const ids = new Set(flagged.map((flag) => flag.id));
    setRecipients((prev) => prev.filter((row) => !ids.has(row.id)));
    setNotice(
      `Removed ${flagged.length} flagged line${flagged.length === 1 ? '' : 's'} from the batch.`,
    );
  };

  const continueToReview = () => {
    if (!report) return;
    const { rows, summary } = applyRowValidation(recipients, channel);
    setRecipients(rows);
    const { ready, flagged } = summary;
    setNotice(
      flagged.length > 0
        ? `${ready} row${ready === 1 ? '' : 's'} ready \u00b7 ${flagged.length} flagged line${
            flagged.length === 1 ? '' : 's'
          } skipped.`
        : `${ready} row${ready === 1 ? '' : 's'} validated and ready to review.`,
    );
    setReport(null);
    setReviewConfirmed(false);
    setStep(2);
  };

  const handleAddFromCampaign = (picked: BulkCandidate[]) => {
    const rows = picked
      .filter((candidate) => !recipients.some((row) => row.id === candidate.id))
      .map((candidate) => candidateToRecipient(candidate, channel, description, 'campaign'));
    if (rows.length === 0) return;
    const sweeps = rows.filter((row) => row.advance).length;
    setRecipients((prev) => [...prev, ...rows]);
    setNotice(
      `Added ${rows.length} creator${rows.length === 1 ? '' : 's'} from ${picked[0].campaign}${
        sweeps > 0 ? ` · ${sweeps} with an early payout to reconcile` : ''
      }.`,
    );
  };

  const updateAmount = (id: string, amount: number) => {
    setRecipients((prev) => prev.map((row) => (row.id === id ? { ...row, amount } : row)));
  };

  const updateWalletId = (id: string, walletId: string) => {
    setRecipients((prev) => prev.map((row) => (row.id === id ? { ...row, walletId } : row)));
  };

  const updateRowDescription = (id: string, value: string) => {
    setRecipients((prev) => prev.map((row) => (row.id === id ? { ...row, description: value } : row)));
  };

  const removeRecipient = (id: string) => {
    setRecipients((prev) => prev.filter((row) => row.id !== id));
  };

  const applyUniform = () => {
    if (uniformAmount <= 0) {
      setNotice('Enter an amount above zero before applying a uniform amount.');
      return;
    }
    setRecipients((prev) => prev.map((row) => ({ ...row, amount: uniformAmount })));
    setNotice(`Applied ${uniformAmount} to every recipient.`);
  };

  const loadSavedList = (list: SavedRecipientList) => {
    const wallet = wallets.find((item) => item.currency === list.currency) ?? null;
    setChannel(list.channel);
    if (wallet) setWalletId(wallet.id);
    setRecipients(rehydrateList(list.id, list.recipients));
    setReviewConfirmed(false);
    setNotice(`Loaded "${list.name}" with ${list.recipients.length} recipient${list.recipients.length === 1 ? '' : 's'}.`);
  };

  const saveList = async (name: string) => {
    if (recipients.length === 0) {
      setNotice('Add recipients before saving a list.');
      return;
    }
    setError('');
    try {
      const list = await api<SavedRecipientList>('/recipient-lists', {
        method: 'POST',
        body: JSON.stringify({ name, channel, currency, recipients }),
      });
      setSavedLists((prev) => [list, ...prev]);
      setNotice(`Saved recipient list "${name}".`);
    } catch (saveError) {
      setError(messageOf(saveError, 'Could not save the list.'));
    }
  };

  const downloadTemplate = () => {
    const header =
      channel === 'mobile'
        ? 'name,mobile_number,network,amount,description,payment_date'
        : 'name,wallet_id,wallet_type,amount,description,payment_date';
    const sampleRows =
      channel === 'mobile'
        ? [
            'Peter Kironji,254712345678,M-Pesa,5000,Twiva Payment,',
            'Grace Wambui,254733190776,Airtel Money,3000,Twiva Payment,2026-09-20 09:00',
          ]
        : [
            'Peter Kironji,2314,Ledger,5000,Twiva Payment,',
            'Grace Wambui,BANK-88213,Bank,3000,Twiva Payment,2026-09-20 09:00',
          ];
    const blob = new Blob([`${header}\n${sampleRows.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // A real .csv either way: Excel opens it, while a CSV named .xls makes Excel warn the file is damaged.
    link.download = 'twiva-bulk-template.csv';
    link.click();
    URL.revokeObjectURL(url);
    setNotice(
      `Template downloaded for ${channel === 'mobile' ? 'Mobile Money' : 'Twiva wallet'} uploads \u00b7 add a payment_date to release rows on a specific day and time.`,
    );
  };

  const startOver = () => {
    setCompleted(null);
    setStep(1);
    resetRoster();
    setUniformAmount(0);
    setSchedule(defaultBulkSchedule());
  };

  // What the batch sends: the confirm step asks for its OTP, then submits it with the code.
  const batch = () => ({
    channel,
    sourceWalletId: walletId,
    sourceWalletName: walletName,
    currency,
    schedule,
    recipients: recipients.filter((row) => row.status === 'Valid'),
  });

  const finish = (transfer: BulkTransfer) => {
    const shown = forDisplay(transfer);
    setCompleted(shown);
    setTransfers((prev) => [shown, ...prev]);
    setOtp('');
  };

  // Review → Confirm: production sends the OTP when the batch is confirmed (or pays at once without one).
  const requestOtp = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ requiresOtp: boolean; transfer?: BulkTransfer }>('/bulk-transfers/otp', {
        method: 'POST',
        body: JSON.stringify(batch()),
      });
      if (!result.requiresOtp && result.transfer) {
        finish(result.transfer);
        return;
      }
      setOtp('');
      setStep(3);
    } catch (otpError) {
      setError(messageOf(otpError, 'Could not start the payment.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyAndComplete = async () => {
    setBusy(true);
    setError('');
    try {
      const transfer = await api<BulkTransfer>('/bulk-transfers', {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify({ ...batch(), otp }),
      });
      finish(transfer);
    } catch (verifyError) {
      setError(messageOf(verifyError, 'Could not complete the payment.'));
    } finally {
      setBusy(false);
    }
  };

  const cancelOtp = async () => {
    setStep(2);
    setOtp('');
    await api('/bulk-transfers/otp/cancel', { method: 'POST' }).catch(() => undefined);
  };

  const walletAccount = sourceWallet ? `${sourceWallet.currency} float · N/A` : '—';

  // Move a scheduled batch to a new release day and time.
  const rescheduleTransfer = async (transfer: BulkTransfer, next: BulkSchedule) => {
    setError('');
    try {
      const updated = forDisplay(
        await api<BulkTransfer>(`/bulk-transfers/${transfer.id}/schedule`, {
          method: 'PATCH',
          body: JSON.stringify({ date: next.date, time: next.time }),
        }),
      );
      setTransfers((prev) => prev.map((item) => (item.id === transfer.id ? updated : item)));
      setNotice(`Batch ${transfer.reference} rescheduled for ${updated.scheduledFor}.`);
    } catch (scheduleError) {
      setError(messageOf(scheduleError, 'Could not reschedule the batch.'));
    } finally {
      setRescheduling(null);
    }
  };

  // Pull a scheduled batch before it goes out — funds stay in the source wallet.
  const cancelTransfer = async (transfer: BulkTransfer) => {
    setError('');
    try {
      const updated = forDisplay(await api<BulkTransfer>(`/bulk-transfers/${transfer.id}/cancel`, { method: 'POST' }));
      setTransfers((prev) => prev.map((item) => (item.id === transfer.id ? updated : item)));
      setNotice(`Batch ${transfer.reference} cancelled — no funds were released.`);
    } catch (cancelError) {
      setError(messageOf(cancelError, 'Could not cancel the batch.'));
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Bulk Payments
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Send one batch by wallet or mobile money — upload, review, then confirm with an OTP.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-exchange-dollar-line" onClick={() => navigate('/payments/transactions')}>
            Transactions
          </Button>
        </div>
      </div>

      {completed ? (
        <BulkTransferSuccess transfer={completed} onNew={startOver} />
      ) : (
        <>
          <div className="mt-6">
            <BulkStepsHeader current={step} />
          </div>

          {notice ? (
            <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
              <span className="inline-flex items-center gap-2 text-sm text-accent-800">
                <i className="ri-checkbox-circle-line" />
                <span className="whitespace-pre-line">{notice}</span>
              </span>
              <button
                type="button"
                onClick={() => setNotice('')}
                className="cursor-pointer text-accent-700 hover:text-accent-900"
                aria-label="Dismiss"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ) : null}

          {error ? (
            <div
              role="alert"
              className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
            >
              <span className="inline-flex items-center gap-2 text-sm text-primary-800">
                <i className="ri-error-warning-line" />
                <span className="whitespace-pre-line">{error}</span>
              </span>
              <button
                type="button"
                onClick={() => setError('')}
                className="cursor-pointer text-primary-700 hover:text-primary-900"
                aria-label="Dismiss"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ) : null}

          {!canManage ? (
            <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
              <i className="ri-lock-line" />
              Your role can view bulk payments but not create them — switch to Finance or
              Administrator.
            </div>
          ) : null}

          {step === 1 ? (
            <>
              <UploadStep
                wallets={fundingWallets}
                walletId={walletId}
                onWallet={handleWallet}
                channel={channel}
                onChannel={handleChannel}
                currency={currency}
                balance={sourceWallet?.balance ?? 0}
                method={method}
                onMethod={setMethod}
                recipients={recipients}
                candidates={addableCandidates}
                waiting={waitingCandidates}
                campaigns={campaignOptions}
                description={description}
                onDescription={setDescription}
                uniformAmount={uniformAmount}
                onUniformAmount={setUniformAmount}
                onApplyUniform={applyUniform}
                onAdd={addCandidate}
                onFileParse={handleFileParse}
                onAddFromCampaign={handleAddFromCampaign}
                readyCandidates={readyCandidates}
                campaignWaiting={campaignWaiting}
                onWalletId={updateWalletId}
                onAmount={updateAmount}
                onRowDescription={updateRowDescription}
                onRemove={removeRecipient}
                sms={sms}
                onSms={setSms}
                savedLists={savedLists}
                onLoadList={loadSavedList}
                onSaveList={saveList}
                totals={totals}
                onPreview={openPreviewReport}
                onDownloadTemplate={downloadTemplate}
                canManage={canManage}
              />
              <div className="mt-6">
                <BulkTransferHistory
                  transfers={transfers}
                  onReschedule={setRescheduling}
                  onCancel={setCancelling}
                />
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <ReviewStep
              recipients={recipients}
              currency={currency}
              channel={channel}
              totals={totals}
              walletName={walletName}
              walletBalance={sourceWallet?.balance ?? 0}
              walletAccount={walletAccount}
              pool={{
                accountName: pool?.accountName ?? '',
                accountNumber: pool?.accountNumber ?? '',
                bankName: pool?.bankName ?? '',
              }}
              schedule={schedule}
              onSchedule={setSchedule}
              reviewConfirmed={reviewConfirmed}
              onReviewConfirmed={setReviewConfirmed}
              onInitiate={() => {
                if (!busy) void requestOtp();
              }}
              onBack={() => setStep(1)}
              sendBlocked={API_MODE === 'agency' && !canApprove}
              canApprove={canApprove}
            />
          ) : null}

          {step === 3 ? (
            <ConfirmStep
              recipientCount={recipients.length}
              total={totals.total}
              currency={currency}
              walletName={walletName}
              otp={otp}
              onOtp={setOtp}
              onVerify={() => {
                if (!busy) void verifyAndComplete();
              }}
              onCancel={() => void cancelOtp()}
              canApprove={canApprove}
              schedule={schedule}
            />
          ) : null}

          {report ? (
            <ValidationReportModal
              label={report.label}
              backLabel={report.backLabel}
              channel={channel}
              currency={currency}
              rows={recipients}
              onFixRow={fixRow}
              onRemoveFlagged={removeFlaggedRows}
              onClose={() => setReport(null)}
              onContinue={continueToReview}
            />
          ) : null}

          <RescheduleBatchModal
            transfer={rescheduling}
            onClose={() => setRescheduling(null)}
            onConfirm={rescheduleTransfer}
          />
          <CancelBatchModal
            transfer={cancelling}
            onClose={() => setCancelling(null)}
            onConfirm={cancelTransfer}
          />
        </>
      )}
    </div>
  );
}